/**
 * The "Compare plans" table on the pricing page — the client's plan sheet
 * (22 Sep 2026), row for row.
 *
 * Rows with a `quantity` read the live number from the plan catalogue
 * (Admin › Subscription plans), so a quota change shows here without a
 * deploy. The ✓ / — / Limited rows are the sheet's own wording, keyed by
 * plan tier; a tier the sheet does not know shows "—".
 *
 * Growth shares Business's column. The four-tier structure (13 Oct 2026) put
 * Growth in Business's place in the ladder and closed Business to new
 * customers, and the two carry the same entitlements, so one set of values
 * describes both. Without the `growth` key every cell fell back to "—" and
 * the pricing page showed the $299 plan as having less than the $99 one.
 */
import type { PlanFeatures } from '../../api/billingApi';

export type ComparisonRow =
  | { label: string; quantity: keyof PlanFeatures; suffix?: string }
  | { label: string; values: Record<string, string> };

export interface ComparisonGroup { title: string; rows: ComparisonRow[] }

const F = 'free', P = 'professional', T = 'growth', B = 'business', E = 'enterprise', G = 'government';
/** `team` fills both Growth (on sale) and Business (legacy, same entitlements). */
const row = (label: string, free: string, pro: string, team: string, ent: string, gov: string): ComparisonRow =>
  ({ label, values: { [F]: free, [P]: pro, [T]: team, [B]: team, [E]: ent, [G]: gov } });
const all = (label: string) => row(label, '✓', '✓', '✓', '✓', '✓');
const paid = (label: string) => row(label, '—', '✓', '✓', '✓', '✓');
const fromTeamTier = (label: string) => row(label, '—', '—', '✓', '✓', '✓');
const fromEnterprise = (label: string) => row(label, '—', '—', '—', '✓', '✓');

export const COMPARISON: ComparisonGroup[] = [
  {
    title: 'Regulatory Intelligence',
    rows: [
      { label: 'Users included', quantity: 'user_seats' },
      row('Regulatory Search', 'Limited', '✓', '✓', '✓', '✓'),
      row('Regulatory Intelligence Dashboard', 'Basic', '✓', '✓', '✓', '✓'),
      row('Regulatory Intelligence Map', 'Limited', '✓', '✓', '✓', '✓'),
      row('Regulatory Activity', 'Basic', '✓', '✓', '✓', '✓'),
      row('Upcoming Laws', 'Limited', '✓', '✓', '✓', '✓'),
      paid('Consultation Periods'),
      paid('Enforcement Dates'),
      row('Regulatory Trends', 'Limited', '✓', '✓', '✓', '✓'),
      row('Country / Jurisdiction Explorer', 'Limited', '✓', '✓', '✓', '✓'),
      { label: 'Jurisdictions covered', quantity: 'jurisdictions' },
      paid('Regulatory Lifecycle Tracking'),
      paid('Amendment / Change Tracking'),
      paid('Text Diff & Regulatory Lineage'),
      fromTeamTier('Cross-Reference Impact Analysis'),
      paid('Effective-Date Warnings'),
    ],
  },
  {
    title: 'AI & Research',
    rows: [
      { label: 'AI Regulatory Chat', quantity: 'ai_summaries_per_month', suffix: '/mo' },
      paid('AI Chat Analysis & Recommendations'),
      all('Grounded AI Responses'),
      all('Source Citations'),
      { label: 'Document Analysis', quantity: 'documents_per_month', suffix: '/mo' },
      row('Regulation Comparison', 'Limited', '✓', '✓', '✓', '✓'),
      paid('Advanced Comparison'),
      paid('Cross-Jurisdiction Comparison'),
      paid('Comparison Matrix'),
      paid('Guided Research'),
    ],
  },
  {
    title: 'Alerts & Monitoring',
    rows: [
      { label: 'Regulatory Alerts', quantity: 'custom_alerts' },
      paid('Smart Alerts'),
      paid('Upcoming Regulation Alerts'),
      paid('Enforcement Alerts'),
      paid('Effective-Date Alerts'),
      paid('Breaking News Alerts'),
      fromTeamTier('Early Warning System'),
    ],
  },
  {
    title: 'Compliance & Risk',
    rows: [
      fromTeamTier('Compliance Context'),
      fromTeamTier('Compliance Baseline'),
      fromTeamTier('Compliance Assessment'),
      fromTeamTier('Risk Assessment'),
      fromTeamTier('Risk / Gap Analysis'),
      fromTeamTier('Evidence-Based Assessment'),
      fromTeamTier('Audit Report'),
      fromTeamTier('Risk Processing & Progress Tracking'),
    ],
  },
  {
    title: 'Policy Generation & AI Agents',
    rows: [
      { label: 'AI Regulatory Agents', quantity: 'ai_agents' },
      { label: 'Policy Generation', quantity: 'policies_per_month', suffix: '/mo' },
      fromTeamTier('Policy Accuracy Tiers'),
      fromTeamTier('Source Hierarchy'),
      fromTeamTier('Generated Output Attribution'),
      fromEnterprise('Reference Model Selector'),
      fromEnterprise('Country Profiles'),
      fromTeamTier('Policy Lifecycle Management'),
    ],
  },
  {
    title: 'Reports & Intelligence Outputs',
    rows: [
      all('Basic Reports'),
      paid('Professional Reports'),
      fromTeamTier('Executive Briefing'),
      fromEnterprise('Board Report'),
      fromEnterprise('Legal Memo'),
      row('Government Report', '—', '—', '—', '—', '✓'),
      paid('Client-Ready Reports'),
      row('Export', 'Basic', '✓', '✓', '✓', '✓'),
    ],
  },
];

/** A catalogue quantity as the table shows it: 0 → "—", unlimited → "Unlimited", else the number. */
export const quantityCell = (v: string | number | boolean | null | undefined, suffix = ''): string => {
  if (v === 'unlimited') return 'Unlimited';
  if (v === 0 || v === '0' || v === null || v === undefined || v === false) return '—';
  return `${v}${suffix}`;
};
